<template>
  <div class="supporting-documents-container">
    <supporting-document-form
      v-if="!isReadonly"
      v-model="currentDocument"
      :id="currentDocumentKey"
      :key="currentDocumentKey"
      @save="handleSave"
      @cancel="resetForm"
    />

    <!-- Lista de documentos -->
    <div v-if="hasDocuments && !isReplacing" class="documents-list">
      <section-title
        class="mt-2 pt-1"
        :has-line="true"
        title="Documentos de Respaldo"
        title-class="font-14"
      />

      <div
        v-for="document in documents"
        :key="document.id"
        class="document-row mt-3"
      >
        <div class="row py-3">
          <div class="col-md-4">
            <label class="data-label mb-0">Nombre del Documento:</label>
            <p class="data-content mt-0">{{ document.name }}</p>
          </div>
          <div class="col-md-3">
            <label class="data-label mb-0">Fecha de Carga:</label>
            <p class="data-content mt-0">{{ document.createdDate | date }}</p>
          </div>
          <div class="col-md-2">
            <label class="data-label mb-0">Usuario:</label>
            <p class="data-content mt-0">{{ document.createdBy }}</p>
          </div>
          <div class="col-md-3">
            <p class="text-right mb-0">
              <action-button
                variant="outline-success mr-2"
                text="Descargar"
                i="fa fa-download font-18"
                class-icon="mx-0"
                @click="handleDownload(document.id)"
              />
              <action-button
                v-if="!isReadonly"
                i="fas fa-times-circle"
                text="Eliminar"
                variant="outline-danger mr-2"
                @click="handleDelete(document.id)"
              />
              <action-button
                v-if="!isReadonly"
                variant="success"
                i="fa fa-refresh font-18"
                text="Reemplazar"
                class-icon="mx-0"
                @click="handleReplace(document)"
              />
            </p>
          </div>
        </div>
      </div>
      <pagination
        v-if="paginationModel.totalItemCount > paginationModel.perPage"
        :data="paginationModel"
        show-text
        @pagination-go-page="fetchDocuments($event)"
      />
    </div>
  </div>
</template>

<script>
import Pagination from 'pagination'
import ActionButton from 'ActionButton';
import SectionTitle from 'SectionTitle';
import DocumentServices from '@/modules/report/document';
import Service from '@/services/api/edendisputeResolution.api';
import SupportingDocumentForm from './SupportingDocumentForm.vue';

const EMPTY_FORM = Object.freeze({
  id: null,
  filename: '',
  name: '',
  file: null,
});

export default {
  name: 'SupportingDocumentsTab',
  components: {
    Pagination,
    ActionButton,
    SectionTitle,
    SupportingDocumentForm,
  },
  props: {
    edenDisputeResolutionId: {
      type: [String, Number],
      default: null,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    documents: [],
    currentDocumentKey: 0,
    currentDocument: { ...EMPTY_FORM },
    paginationModel: {
      perPage: 10,
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 0,
      count: 0,
    },
  }),
  computed: {
    hasDocuments() {
      return this.documents.length > 0;
    },
    isReplacing() {
      return this.currentDocument.id !== null;
    },
  },
  async mounted() {
    await this.fetchDocuments();
  },
  methods: {
    async handleDownload(documentId) {
      await Service.getSupportingDocument(documentId)
      .then((res) => DocumentServices.Download(res.data));
    },
    async handleDelete(documentId) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar el archivo de respaldo?`,
        'Sí',
        'No'
      );

      if (!confirmed) {
        return;
      }

      await Service.deleteSupportingDocument(documentId);
      await this.fetchDocuments();
      
      this.ShowToast(
        "",
        `El archivo de respaldo se eliminó correctamente.`,
        "success"
      );
    },
    async fetchDocuments(pageIndex = 1) {
      this.paginationModel.currentPage = pageIndex
      const { data } = await Service.getSupportingDocuments(
        this.edenDisputeResolutionId,
        this.paginationModel.currentPage,
        this.paginationModel.perPage);

      this.documents = [...data.data];
      this.paginationModel.totalItemCount = data.totalItemCount;
      this.paginationModel.pageCount = data.pageCount;
      this.paginationModel.count = data.count;
    },
    handleReplace(document) {
      this.currentDocument = { ...document };
    },
    resetForm() {
      this.currentDocument = { ...EMPTY_FORM };
      this.currentDocumentKey += 1;
    },
    async handleSave(data) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea confirmar la acción?`,
        'Sí',
        'No'
      );
      
      if (!confirmed) {
        return;
      }

      let formData = new FormData();
      if (data.id) {
        formData.append("id", data.id);
      }

      if (data.file) {
        formData.append("file", data.file);
      }

      formData.append("name", data.name);

      await Service.upsertSupportingDocument(this.edenDisputeResolutionId, formData);
      await this.fetchDocuments();

      this.ShowToast(
        "",
        `El archivo de respaldo se cargó correctamente.`,
        "success"
      );

      this.resetForm();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/_colors.scss';

.supporting-documents-container {
  padding: 1rem;
}

.documents-list {
  margin-top: 1rem;
}

.document-row {
  background-color: #f8f9fa;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
}

.data-label {
  font-weight: bold;
  font-size: 14px;
  color: #11291b;
  margin-bottom: 4px;
}

.data-content {
  font-size: 14px;
  margin-bottom: 0;
}

.font-18 {
  font-size: 18px;
}

// Estilos para el input file personalizado (similar a CustomFileInput)
.file-select {
  text-align: center;
  cursor: pointer;
  width: 100%;
  display: block;
  
  input[type="file"] {
    display: none;
  }
}

.form-control.position-relative {
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f8f9fa;
    border-color: #2db782;
  }
}

.text-green {
  color: #2db782;
}
</style>
